import { createSelector } from 'reselect';

import { selectUser } from '@pressreader/authentication-core';
import { COLLECTION_IS_PRIVATE_DEFAULT } from '@pressreader/social-types';

/**
 * If profile is private, only the owner user can see their collections.
 * If profile is public, the other users can see the public channel's public collections.
 */
const selectIsOwnChannelPrivate = createSelector(selectUser, user => user?.isProfilePrivate ?? COLLECTION_IS_PRIVATE_DEFAULT);

interface CurrentProfileRouteMeta {
    /**
     * Whether the viewed profile route belongs to the currently authenticated user.
     */
    isCurrentUser: boolean;
    /**
     * Privacy state from route profile metadata.
     */
    isProfilePrivate: boolean;
}

/**
 * Read and normalize current profile route metadata from an unknown route payload.
 * Returns `null` when the payload is missing or does not contain route privacy metadata.
 * If `isProfilePrivate` exists but `isCurrentUser` is missing, ownership defaults to `false`.
 */
function readCurrentProfileRouteMeta(currentProfile: unknown): CurrentProfileRouteMeta | null {
    if (!currentProfile || typeof currentProfile !== 'object') {
        return null;
    }

    const { isCurrentUser, isProfilePrivate } = currentProfile as Partial<CurrentProfileRouteMeta>;
    if (typeof isCurrentUser === 'boolean' && typeof isProfilePrivate === 'boolean') {
        return { isCurrentUser, isProfilePrivate };
    }

    if (typeof isProfilePrivate === 'boolean') {
        return { isCurrentUser: false, isProfilePrivate };
    }

    return null;
}

/**
 * Resolves channel privacy by source priority:
 * route/profile privacy -> own channel privacy (when ownership is known) -> private fallback when route privacy is unknown.
 */
function resolveCurrentChannelPrivateFromRouteOrOwnFallback({
    isMyOwnChannel,
    isViewedChannelPrivate,
    isOwnChannelPrivate,
}: {
    isMyOwnChannel: boolean;
    isViewedChannelPrivate: boolean | undefined;
    isOwnChannelPrivate: boolean;
}): boolean {
    if (isViewedChannelPrivate !== undefined) {
        return isViewedChannelPrivate;
    }

    if (isMyOwnChannel) {
        return isOwnChannelPrivate;
    }

    return COLLECTION_IS_PRIVATE_DEFAULT;
}

export { readCurrentProfileRouteMeta, resolveCurrentChannelPrivateFromRouteOrOwnFallback, selectIsOwnChannelPrivate };
